import React, { useEffect, useRef, useState } from "react";
import {
  getMeetingShare,
  removeMeetingShareAccess,
  shareMeetingReport,
} from "../../services/shareMeetingService";
import "./shareMeetingReportModal.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function GlobeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="#425259" strokeWidth="1.6" />
      <path
        d="M3 12h18M12 3c2.5 2.8 3.8 5.8 3.8 9S14.5 18.2 12 21c-2.5-2.8-3.8-5.8-3.8-9S9.5 5.8 12 3z"
        stroke="#425259"
        strokeWidth="1.6"
      />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect
        x="5"
        y="10"
        width="14"
        height="10"
        rx="2"
        stroke="#425259"
        strokeWidth="1.6"
      />
      <path
        d="M8 10V7a4 4 0 018 0v3"
        stroke="#425259"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CloseIcon({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" fill="none" aria-hidden>
      <path
        d="M2 2l8 8M10 2L2 10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <circle cx="8" cy="3" r="1.3" fill="#667085" />
      <circle cx="8" cy="8" r="1.3" fill="#667085" />
      <circle cx="8" cy="13" r="1.3" fill="#667085" />
    </svg>
  );
}

function Spinner({ size = 18, light = false }) {
  return (
    <span
      className={`mg-share-spinner${light ? " mg-share-spinner--light" : ""}`}
      style={{ width: size, height: size }}
      aria-hidden
    />
  );
}

export default function ShareMeetingReportModal({
  open,
  onClose,
  roomName,
  meetingTitle,
  companyId,
  recruiterId,
  canShare = true,
}) {
  const [accessType, setAccessType] = useState("anyone");
  const [accessOpen, setAccessOpen] = useState(false);
  const [emails, setEmails] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [shareData, setShareData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [menuEmail, setMenuEmail] = useState(null);
  const accessRef = useRef(null);
  const menuRef = useRef(null);

  const title = meetingTitle || "Meeting report";

  useEffect(() => {
    if (!open || !roomName || !companyId || !recruiterId) return;
    let alive = true;
    setInitialLoading(true);
    setError("");
    setSuccess("");
    setCopied(false);
    setAccessOpen(false);
    setMenuEmail(null);
    getMeetingShare(roomName, { companyId, recruiterId })
      .then((data) => {
        if (!alive) return;
        const share = data?.share || null;
        setShareData(share);
        setAccessType(share?.access_type === "restricted" ? "restricted" : "anyone");
        const emailList = Array.isArray(share?.shared_with)
          ? share.shared_with
              .filter((g) => g?.type === "meetgeek" || !g?.type)
              .map((g) => String(g.email || "").toLowerCase())
              .filter(Boolean)
          : [];
        setEmails(emailList);
      })
      .catch((e) => {
        if (alive) {
          setShareData(null);
          setAccessType("anyone");
          setEmails([]);
          setError(e.message || "Failed to load share settings");
        }
      })
      .finally(() => {
        if (alive) setInitialLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [open, roomName, companyId, recruiterId]);

  const reset = () => {
    setAccessType("anyone");
    setAccessOpen(false);
    setEmails([]);
    setInputValue("");
    setShareData(null);
    setCopied(false);
    setLoading(false);
    setRemoveLoading(false);
    setError("");
    setSuccess("");
    setMenuEmail(null);
  };

  const close = () => {
    if (loading || removeLoading) return;
    reset();
    onClose?.();
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (menuEmail) setMenuEmail(null);
        else if (accessOpen) setAccessOpen(false);
        else if (!loading && !removeLoading) close();
      }
    };
    const onDoc = (e) => {
      if (accessRef.current && !accessRef.current.contains(e.target)) {
        setAccessOpen(false);
      }
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuEmail(null);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDoc);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDoc);
    };
  }, [open, accessOpen, loading, removeLoading, menuEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const addEmail = (raw) => {
    const email = String(raw || "")
      .trim()
      .toLowerCase();
    if (!email || !EMAIL_RE.test(email)) {
      if (email) setError("Enter a valid email.");
      return;
    }
    const persisted = (shareData?.shared_with || []).some(
      (item) =>
        (item?.type === "meetgeek" || !item?.type) &&
        String(item?.email || "").toLowerCase() === email,
    );
    if (persisted || emails.includes(email)) {
      setInputValue("");
      setSuccess("This email already has access.");
      return;
    }
    setEmails((prev) => [...prev, email]);
    setInputValue("");
    setError("");
    setSuccess("");
  };

  const removeEmail = async (emailToRemove) => {
    if (!emailToRemove) return;
    const normalized = emailToRemove.toLowerCase();
    const isPersisted = (shareData?.shared_with || []).some(
      (item) =>
        (item?.type === "meetgeek" || !item?.type) &&
        String(item?.email || "").toLowerCase() === normalized,
    );

    if (!shareData?.id || !isPersisted) {
      setEmails((prev) => prev.filter((e) => e !== emailToRemove));
      setMenuEmail(null);
      return;
    }

    try {
      setRemoveLoading(true);
      setError("");
      await removeMeetingShareAccess(roomName, {
        companyId,
        recruiterId,
        email: emailToRemove,
      });
      setEmails((prev) => prev.filter((e) => e !== emailToRemove));
      const updatedSharedWith = (shareData?.shared_with || []).filter(
        (item) =>
          !(
            String(item?.email || "").toLowerCase() === normalized &&
            (item?.type === "meetgeek" || !item?.type)
          ),
      );
      setShareData({ ...shareData, shared_with: updatedSharedWith });
      setSuccess("Access removed. Their link will no longer work.");
    } catch (e) {
      setError(e.message || "Failed to remove access");
    } finally {
      setRemoveLoading(false);
      setMenuEmail(null);
    }
  };

  const copyLinkToClipboard = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setSuccess("Link copied to clipboard.");
      setTimeout(() => setCopied(false), 2000);
      return true;
    } catch {
      setError("Could not copy link.");
      return false;
    }
  };

  const handleSend = async () => {
    if (!canShare) {
      setError("Share is available only after the Sage summary is ready.");
      return;
    }
    setError("");
    setSuccess("");

    // Anyone — copy existing public URL when present (ShareResultModal parity)
    if (accessType === "anyone" && shareData?.public_url_meetgeek) {
      await copyLinkToClipboard(shareData.public_url_meetgeek);
      return;
    }

    if (accessType === "restricted") {
      const already = new Set(
        (shareData?.shared_with || [])
          .filter((item) => item?.type === "meetgeek" || !item?.type)
          .map((item) => String(item?.email || "").toLowerCase()),
      );
      const newEmails = emails.filter((e) => !already.has(e.toLowerCase()));
      if (newEmails.length === 0) {
        setSuccess("Already shared with these people.");
        return;
      }
    }

    setLoading(true);
    try {
      const guests =
        accessType === "restricted"
          ? emails.map((email) => ({
              name: email.split("@")[0],
              email,
            }))
          : [];
      const data = await shareMeetingReport(roomName, {
        companyId,
        recruiterId,
        accessType,
        guests,
        emails: accessType === "restricted" ? emails : [],
      });
      setShareData(data);
      if (accessType === "anyone") {
        const url = data?.public_url_meetgeek || "";
        if (url) {
          await copyLinkToClipboard(url);
        } else {
          setError("Share created but no public link was returned.");
        }
      } else {
        setSuccess("Invites sent. Recipients must verify with OTP to view.");
        setTimeout(() => close(), 900);
      }
    } catch (e) {
      setError(e.message || "Failed to share");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const busy = loading || initialLoading || removeLoading;
  const accessLabel =
    accessType === "anyone" ? "Anyone with the link" : "Restricted";

  return (
    <div className="mg-share-overlay" role="presentation" onClick={close}>
      <div
        className="mg-share-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mg-share-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mg-share-modal__head">
          <h2 id="mg-share-title" title={title}>
            Share “{title}”
          </h2>
          <button
            type="button"
            className="mg-share-modal__close"
            onClick={close}
            aria-label="Close"
            disabled={loading || removeLoading}
          >
            <CloseIcon size={12} />
          </button>
        </header>

        <div className="mg-share-modal__body">
          {initialLoading ? (
            <div className="mg-share-loading" role="status">
              <Spinner size={22} />
              <span>Loading share settings…</span>
            </div>
          ) : (
            <>
              {!canShare ? (
                <p className="mg-share-modal__hint">
                  Sharing unlocks when the Sage AI summary is ready for this
                  meeting.
                </p>
              ) : null}

              {accessType === "restricted" ? (
                <div className="mg-share-email-field">
                  <input
                    type="email"
                    dir="ltr"
                    placeholder="Enter email and press Enter"
                    value={inputValue}
                    disabled={busy || !canShare}
                    onChange={(e) => {
                      const next = e.target.value;
                      if (next.endsWith(" ") && EMAIL_RE.test(next.trim())) {
                        addEmail(next.trim());
                        return;
                      }
                      setInputValue(next);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addEmail(inputValue);
                      }
                    }}
                    onBlur={() => {
                      if (inputValue.trim()) addEmail(inputValue);
                    }}
                  />
                </div>
              ) : null}

              <p className="mg-share-label">Who has access</p>
              <div className="mg-share-access" ref={accessRef}>
                <button
                  type="button"
                  className="mg-share-access__trigger"
                  disabled={busy || !canShare}
                  aria-haspopup="listbox"
                  aria-expanded={accessOpen}
                  onClick={() => setAccessOpen((v) => !v)}
                >
                  <span className="mg-share-access__value">
                    {accessType === "anyone" ? <GlobeIcon /> : <LockIcon />}
                    {accessLabel}
                  </span>
                  <span className="mg-share-access__chevron" aria-hidden>
                    ▾
                  </span>
                </button>
                {accessOpen ? (
                  <ul className="mg-share-access__menu" role="listbox">
                    <li>
                      <button
                        type="button"
                        className={accessType === "anyone" ? "is-active" : ""}
                        onClick={() => {
                          setAccessType("anyone");
                          setAccessOpen(false);
                          setCopied(false);
                          setSuccess("");
                        }}
                      >
                        <GlobeIcon />
                        Anyone with the link
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className={
                          accessType === "restricted" ? "is-active" : ""
                        }
                        onClick={() => {
                          setAccessType("restricted");
                          setAccessOpen(false);
                          setCopied(false);
                          setSuccess("");
                        }}
                      >
                        <LockIcon />
                        Restricted
                      </button>
                    </li>
                  </ul>
                ) : null}
              </div>

              {emails.length > 0 ? (
                <>
                  <p className="mg-share-label">People with access</p>
                  <ul
                    className={`mg-share-guest-list${
                      emails.length > 4 ? " is-scrollable" : ""
                    }`}
                  >
                    {emails.map((email) => (
                      <li key={email}>
                        <span
                          className="mg-share-avatar"
                          style={{ background: "#4DD0E1" }}
                          aria-hidden
                        >
                          {email.charAt(0).toUpperCase()}
                        </span>
                        <span className="mg-share-guest-list__meta">
                          <strong>{email}</strong>
                        </span>
                        <div
                          className="mg-share-menu-wrap"
                          ref={menuEmail === email ? menuRef : null}
                        >
                          <button
                            type="button"
                            className="mg-share-guest-list__more"
                            aria-label={`Options for ${email}`}
                            disabled={busy}
                            onClick={() =>
                              setMenuEmail((cur) =>
                                cur === email ? null : email,
                              )
                            }
                          >
                            <MoreIcon />
                          </button>
                          {menuEmail === email ? (
                            <div className="mg-share-menu">
                              <button
                                type="button"
                                onClick={() => void removeEmail(email)}
                              >
                                <span className="mg-share-menu__icon">
                                  <CloseIcon size={12} />
                                </span>
                                Remove access
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <div className="mg-share-public-help">
                  <span className="mg-share-public-help__icon" aria-hidden>
                    {accessType === "anyone" ? <GlobeIcon /> : <LockIcon />}
                  </span>
                  <p>
                    {accessType === "anyone"
                      ? "Anyone with the link can view this meeting report."
                      : "No one has access yet. Add emails above."}
                  </p>
                </div>
              )}

              {error ? <p className="mg-share-modal__error">{error}</p> : null}
              {success ? (
                <p className="mg-share-modal__success">{success}</p>
              ) : null}
            </>
          )}
        </div>

        <footer className="mg-share-modal__foot">
          {accessType === "restricted" && emails.length > 0 ? (
            <button
              type="button"
              className="mg-share-btn mg-share-btn--ghost"
              disabled={busy}
              onClick={close}
            >
              Cancel
            </button>
          ) : null}
          <button
            type="button"
            className="mg-share-btn mg-share-btn--primary"
            disabled={
              busy ||
              !canShare ||
              (accessType === "restricted" && emails.length === 0)
            }
            onClick={() => {
              if (accessType === "restricted" && emails.length === 0) {
                close();
                return;
              }
              void handleSend();
            }}
          >
            {loading ? (
              <Spinner size={20} light />
            ) : copied ? (
              "Copied"
            ) : accessType === "anyone" ? (
              "Copy link"
            ) : emails.length > 0 ? (
              "Send"
            ) : (
              "Done"
            )}
          </button>
        </footer>
      </div>
    </div>
  );
}
